import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

const translations = {
  id: {
    // Dashboard
    'good_morning': 'Selamat Pagi',
    'good_afternoon': 'Selamat Siang',
    'good_evening': 'Selamat Sore',
    'good_night': 'Selamat Malam',
    'real_time': 'Waktu Nyata',
    'admin_panel': 'Panel Admin',

    // Navigation
    'dashboard': 'Dashboard',
    'users': 'Pengguna',
    'properties': 'Properti',
    'bookings': 'Pemesanan',
    'payments': 'Pembayaran',
    'reports': 'Laporan',
    'analytics': 'Analitik',
    'settings': 'Pengaturan',
    'activity_logs': 'Log Aktivitas',
    'user_management': 'Manajemen Pengguna',
    'management': 'Manajemen',
    'system': 'Sistem',
    'logout': 'Keluar',

    // Stats
    'total_users': 'Total Pengguna',
    'total_properties': 'Total Properti',
    'total_bookings': 'Total Pemesanan',
    'total_revenue': 'Total Pendapatan',
    'active_bookings': 'Pemesanan Aktif',
    'pending_bookings': 'Pemesanan Menunggu',
    'available_properties': 'Properti Tersedia',
    'rented_properties': 'Properti Disewa',
    'active': 'Aktif',
    'pending': 'Menunggu',
    'available': 'Tersedia',
    'rented': 'Disewa',

    // Dashboard specific
    'recent_bookings': 'Pemesanan Terbaru',
    'quick_actions': 'Aksi Cepat',
    'manage_users': 'Kelola Pengguna',
    'properties_management': 'Manajemen Properti',
    'view_bookings': 'Lihat Pemesanan',
    'view_payments': 'Lihat Pembayaran',
    'no_recent_bookings': 'Tidak ada pemesanan terbaru',
    'view': 'Tampilan',
    'cards': 'Kartu',
    'table': 'Tabel',
    'no_properties_found': 'Tidak Ada Properti',
    'try_adjusting_search': 'Coba sesuaikan pencarian atau filter',
    'manage_all_property_listings': 'Kelola semua daftar properti',
    'last_30_days': '30 Hari Terakhir',
    'last_7_days': '7 Hari Terakhir',
    'last_90_days': '90 Hari Terakhir',
    'last_year': 'Tahun Lalu',
    'confirm_logout': 'Konfirmasi Logout',
    'confirm_logout_message': 'Apakah Anda yakin ingin keluar dari sistem?',
    'yes_logout': 'Ya, Logout',
    'track_all_activities': 'Lacak semua aktivitas manager dan sistem',
    'all_activities': 'Semua Aktivitas',
    'updates': 'Pembaruan',
    'approvals': 'Persetujuan',
    'created': 'Dibuat',
    'no_activities_found': 'Tidak Ada Aktivitas',
    'no_activities_recorded': 'Belum ada aktivitas sistem yang tercatat',
    'no_activities_match': 'Tidak ada aktivitas yang cocok dengan filter',

    // Common
    'loading': 'Memuat...',
    'no_data': 'Tidak ada data tersedia',
    'search': 'Cari',
    'filter': 'Filter',
    'export': 'Ekspor',
    'add_new': 'Tambah Baru',
    'edit': 'Edit',
    'delete': 'Hapus',
    'save': 'Simpan',
    'cancel': 'Batal',
    'confirm': 'Konfirmasi',
    'status': 'Status',
    'actions': 'Aksi',
    'all': 'Semua',
    'records': 'data',
    'name': 'Name',
    'email': 'Email',
    'phone': 'Phone',
    'address': 'Address',
    'date': 'Date',
    'amount': 'Amount',
    'description': 'Description',

    // All Pages
    'property_management': 'Manajemen Properti',
    'booking_management': 'Manajemen Pemesanan',
    'payment_management': 'Manajemen Pembayaran',
    'notification_center': 'Pusat Notifikasi',
    'system_health': 'Kesehatan Sistem',
    'backup_restore': 'Backup & Restore',
    'file_manager': 'Manajer File',
    'messages': 'Pesan',
    'notifications': 'Notifikasi',

    // User Management
    'manage_monitor_users': 'Kelola dan pantau akun pengguna dengan kontrol lanjutan',
    'add_user': 'Tambah Pengguna',
    'edit_user': 'Edit Pengguna',
    'suspend_user': 'Tangguhkan Pengguna',
    'activate_user': 'Aktifkan Pengguna',
    'user_details': 'Detail Pengguna',
    'joined': 'Bergabung',
    'inactive': 'Tidak Aktif',

    // Properties
    'add_property': 'Tambah Properti',
    'edit_property': 'Edit Properti',
    'property_details': 'Detail Properti',
    'property_type': 'Jenis Properti',
    'bedrooms': 'Kamar Tidur',
    'bathrooms': 'Kamar Mandi',
    'area': 'Luas',
    'price': 'Harga',
    'facilities': 'Fasilitas',
    'location': 'Lokasi',
    'loading_properties': 'Memuat properti...',
    'manage_all_properties': 'Kelola semua daftar properti dan detail',
    'mark_available': 'Tandai Tersedia',
    'size': 'Ukuran',
    'image': 'Gambar',
    'property': 'Properti',
    'maintenance': 'Pemeliharaan',
    'manage_property_info': 'Kelola informasi dan detail properti',
    'property_name': 'Nama Properti',
    'property_city': 'Kota',
    'property_description': 'Deskripsi',
    'property_address': 'Alamat',
    'property_price': 'Harga (Rp/bulan)',
    'property_bedrooms': 'Kamar Tidur',
    'property_bathrooms': 'Kamar Mandi',
    'property_size': 'Luas (m²)',
    'property_image': 'Gambar Properti',
    'property_status': 'Status',
    'enter_property_name': 'Masukkan nama properti',
    'enter_city': 'Masukkan kota',
    'enter_description': 'Masukkan deskripsi',
    'enter_address': 'Masukkan alamat lengkap',
    'optional': 'opsional',
    'choose_image_file_property': 'Pilih file gambar (JPG, PNG, GIF, WebP, maks 2MB, min 100x100px)',
    'new_image': 'Gambar Baru',
    'current_image': 'Gambar Saat Ini',
    'invalid_image': 'Gambar tidak valid',
    'selected': 'Terpilih',
    'create_property': 'Buat Properti',
    'update_property': 'Perbarui Properti',
    'saving': 'Menyimpan...',
    'manage_user_info': 'Kelola informasi akun pengguna',
    'profile_photo': 'Foto Profil',
    'new_photo': 'Foto Baru',
    'current_photo': 'Foto Saat Ini',
    'invalid_photo': 'Gambar tidak valid',
    'choose_image_file_user': 'Pilih file gambar (JPG, PNG, GIF, WebP, maks 2MB, min 100x100px)',
    'selected_file': 'Dipilih',
    'full_name': 'Nama Lengkap',
    'enter_full_name': 'Masukkan nama lengkap',
    'email_address': 'Alamat Email',
    'enter_email': 'Masukkan alamat email',
    'password': 'Kata Sandi',
    'enter_password': 'Masukkan kata sandi',
    'keep_current_password': 'Kosongkan untuk mempertahankan kata sandi saat ini',
    'phone_number': 'Nomor Telepon',
    'account_status': 'Status Akun',
    'create_user': 'Buat Pengguna',
    'update_user': 'Perbarui Pengguna',
    'manage_booking_info': 'Kelola detail pemesanan',
    'edit_booking': 'Edit Booking',
    'add_booking': 'Tambah Booking',
    'select_user': 'Pilih Pengguna',
    'select_property': 'Pilih Properti',
    'start_date': 'Tanggal Mulai',
    'end_date': 'Tanggal Selesai',
    'end_date_after_start': 'Tanggal selesai harus setelah tanggal mulai!',
    'total_cost': 'Total Biaya',
    'nights': 'malam',
    'guests': 'Jumlah Tamu',
    'create_booking': 'Buat Booking',
    'update_booking': 'Perbarui Booking',
    'manage_payment_info': 'Kelola informasi pembayaran',
    'edit_payment': 'Edit Pembayaran',
    'add_payment': 'Tambah Pembayaran',
    'select_booking': 'Pilih Booking',
    'payment_amount': 'Jumlah',
    'payment_method_label': 'Metode Pembayaran',
    'select_payment_method': 'Pilih Metode Pembayaran',
    'transaction_id_label': 'ID Transaksi',
    'optional_field': 'Opsional',
    'user_label': 'Pengguna',
    'property_label': 'Properti',
    'total_amount_label': 'Total Jumlah',
    'paid_amount': 'Jumlah Dibayar',
    'remaining_amount': 'Sisa',
    'max_amount': 'Maks',
    'create_payment': 'Buat Pembayaran',
    'update_payment': 'Perbarui Pembayaran',
    'manage_all_bookings': 'Kelola semua permintaan pemesanan dan reservasi',
    'booking': 'Pemesanan',
    'user': 'Pengguna',
    'dates': 'Tanggal',
    'no_bookings_found': 'Tidak Ada Pemesanan',
    'approve': 'Setujui',
    'rejected': 'Ditolak',
    'completed': 'Selesai',
    'approved': 'Disetujui',
    'cancelled': 'Dibatalkan',
    'manage_all_payments': 'Kelola semua transaksi pembayaran dan kwitansi',
    'payment': 'Pembayaran',
    'booking_id': 'ID Pemesanan',
    'transaction_id': 'ID Transaksi',
    'no_payments_found': 'Tidak Ada Pembayaran',
    'mark_completed': 'Tandai Selesai',
    'failed': 'Gagal',
    'loading_analytics': 'Memuat analitik...',
    'revenue_per_property': 'Pendapatan per Properti',
    'avg_per_property': 'Rata-rata per Properti',
    'active_users': 'Pengguna Aktif',
    'booking_statistics': 'Statistik Booking',
    'conversion_rate_label': 'Tingkat Konversi',
    'customer_retention': 'Retensi Pelanggan',
    'booking_revenue_trends': 'Tren Booking & Pendapatan (6 Bulan Terakhir)',
    'revenue_label': 'Pendapatan',
    'performance_summary': 'Ringkasan Performa',
    'booking_growth': 'Pertumbuhan Booking',
    'repeat_users': 'Pengguna Berulang',
    'avg_occupancy': 'Rata-rata Hunian',
    'top_properties_label': 'Properti Terbaik',
    'occupancy': 'Hunian',
    'average': 'Rata-rata',
    'total_properties_label': 'Total Properti',
    'units_available': 'Unit tersedia',
    'total_bookings_label': 'Total Booking',
    'from_last_month': 'dari bulan lalu',
    'occupancy_rate_label': 'Tingkat Hunian',
    'avg_occupancy_label': 'Rata-rata hunian',
    'total_revenue_label': 'Total Pendapatan',
    'growth': 'pertumbuhan',
    'manage_account_settings': 'Kelola pengaturan akun dan preferensi Anda',
    'profile_settings': 'Pengaturan Profil',
    'current_profile_photo': 'Foto Profil Saat Ini',
    'photo_appears_header': 'Foto ini muncul di header',
    'recommended_square': 'Disarankan: Gambar persegi, min 200x200px',
    'choose_new_photo': 'Pilih Foto Profil Baru',
    'choose_image_file': 'Pilih file gambar (JPG, PNG, GIF, maks 2MB)',
    'preview': 'Pratinjau',
    'updating': 'Memperbarui...',
    'update_photo': 'Perbarui Foto',
    'account_information': 'Informasi Akun',
    'role': 'Peran',
    'enter_current_password': 'Masukkan kata sandi saat ini di bawah untuk menyimpan perubahan',
    'enter_password_confirm': 'Masukkan kata sandi saat ini untuk mengonfirmasi perubahan',
    'enter_password_to_save': 'Masukkan kata sandi saat ini di bawah untuk menyimpan perubahan',
    'confirm_new_password_placeholder': 'Konfirmasi kata sandi baru',
    'change_password': 'Ubah Kata Sandi',
    'current_password': 'Kata Sandi Saat Ini',
    'enter_current_pass': 'Masukkan kata sandi saat ini',
    'new_password': 'Kata Sandi Baru',
    'enter_new_password': 'Masukkan kata sandi baru',
    'confirm_new_password': 'Konfirmasi Kata Sandi Baru',
    'confirm_new_pass': 'Konfirmasi kata sandi baru',
    'changing_password': 'Mengubah Kata Sandi...',
    'confirm_profile_update': 'Konfirmasi Pembaruan Profil',
    'confirm_profile_message': 'Apakah Anda yakin ingin memperbarui informasi profil Anda? Ini akan mengubah detail akun Anda.',
    'update_profile': 'Perbarui Profil',
    'confirm_password_change': 'Konfirmasi Perubahan Kata Sandi',
    'confirm_password_message': 'Apakah Anda yakin ingin mengubah kata sandi Anda? Anda perlu menggunakan kata sandi baru untuk login di masa mendatang.',
    'change_password_btn': 'Ubah Kata Sandi',
    'no_data_export': 'Tidak ada data untuk diekspor',
    'export_failed': 'Ekspor gagal',
    'no_data_available': 'Tidak ada data tersedia',
    'pdf_export_failed': 'Ekspor PDF gagal',
    'generating_pdf': 'Membuat PDF...',
    'exporting': 'Mengekspor...',

    // Bookings
    'booking_details': 'Detail Pemesanan',
    'check_in': 'Check In',
    'check_out': 'Check Out',
    'guest_name': 'Nama Tamu',
    'total_amount': 'Total Jumlah',
    'booking_date': 'Tanggal Pemesanan',
    'confirmed': 'Dikonfirmasi',
    'paid': 'Dibayar',
    'unpaid': 'Belum Dibayar',
    'refunded': 'Dikembalikan',

    // Analytics
    'analytics_reports': 'Analitik & Laporan',
    'arpu': 'Pendapatan per Pengguna',
    'customer_ltv': 'Nilai Seumur Hidup Pelanggan',
    'conversion_rate': 'Tingkat Konversi',
    'market_share': 'Pangsa Pasar',
    'property_growth': 'Pertumbuhan Properti',
    'revenue_trend': 'Tren Pendapatan',
    'executive_summary': 'Ringkasan Eksekutif',
    'customer_segments': 'Segmen Pelanggan',
    'ai_insights': 'Wawasan AI',
    'top_properties': 'Properti Terbaik',

    // Settings
    'general_settings': 'Pengaturan Umum',
    'security_settings': 'Pengaturan Keamanan',
    'notification_settings': 'Pengaturan Notifikasi',
    'system_settings': 'Pengaturan Sistem',
    'language_settings': 'Pengaturan Bahasa',
    'theme_settings': 'Pengaturan Tema',

    // Common Actions
    'reject': 'Tolak',
    'suspend': 'Tangguhkan',
    'restore': 'Pulihkan',
    'download': 'Unduh',
    'upload': 'Unggah',
    'refresh': 'Segarkan',
    'close': 'Tutup',
    'open': 'Buka',
    'submit': 'Kirim',
    'reset': 'Reset',
    'clear': 'Bersihkan',

    // Users Page
    'users_management': 'Manajemen Pengguna',
    'manage_user_accounts': 'Kelola akun pengguna dan izin',
    'users_management_report': 'Laporan Manajemen Pengguna',
    'selected_users_export': 'Ekspor Pengguna Terpilih',
    'generated': 'Dibuat',
    'id': 'ID',
    'no_phone': 'Tidak ada telepon',
    'error_loading_users': 'Error Memuat Pengguna',
    'try_again': 'Coba Lagi',
    'no_users_found': 'Tidak Ada Pengguna',
    'adjust_search_filter': 'Coba sesuaikan kriteria pencarian atau filter',
    'confirm_delete_user': 'Apakah Anda yakin ingin menghapus pengguna ini?',
    'user_deleted_success': 'Pengguna berhasil dihapus!',
    'failed_delete_user': 'Gagal menghapus pengguna',
    'deleted_successfully': 'berhasil dihapus',
    'failed_delete_users': 'Gagal menghapus pengguna',
    'activated_successfully': 'berhasil diaktifkan',
    'failed_activate_users': 'Gagal mengaktifkan pengguna',

    // Pagination
    'showing': 'Menampilkan',
    'of': 'dari',
    'back': 'Kembali',
    'next': 'Selanjutnya',

    // Properties Page
    'properties_management_report': 'Laporan Manajemen Properti',
    'confirm_delete_property': 'Apakah Anda yakin ingin menghapus properti ini?',
    'property_deleted_success': 'Properti berhasil dihapus!',
    'failed_delete_property': 'Gagal menghapus properti',
    'failed_delete_properties': 'Gagal menghapus properti',
    'updated_successfully': 'berhasil diperbarui',
    'failed_update_properties': 'Gagal memperbarui properti',
    'br': 'KT',
    'ba': 'KM',

    // Bookings Page
    'confirm_delete_booking': 'Apakah Anda yakin ingin menghapus booking ini?',
    'booking_deleted_success': 'Booking berhasil dihapus',
    'failed_delete_booking': 'Gagal menghapus booking',
    'error_loading_data': 'Kesalahan Memuat Data',
    'export_details': 'Ekspor Detail',
    'no_bookings_available': 'Belum ada data booking yang tersedia',

    // Reports Page
    'sewaapartemen_analytics_report': 'Laporan Analitik SewaApartemen',
    'generated_on': 'Dibuat pada',
    'comprehensive_business_reports': 'Laporan bisnis dan wawasan data yang komprehensif',
    'monthly': 'Bulanan',
    'quarterly': 'Triwulanan',
    'yearly': 'Tahunan',
    'revenue_overview': 'Ikhtisar Pendapatan',
    'transactions': 'transaksi',
    'bookings_by_status': 'Booking berdasarkan Status',
    'properties_by_status': 'Properti berdasarkan Status',
    'user_statistics': 'Statistik Pengguna',
    'new_this_month': 'Baru Bulan Ini',
    'export_reports': 'Ekspor Laporan',
    'export_to_pdf': 'Ekspor ke PDF',
    'download_comprehensive_report': 'Unduh laporan komprehensif',
    'export_data': 'Ekspor Data',
    'download_raw_data_file': 'Unduh file data mentah',
    'print_report': 'Cetak Laporan',
    'print_current_view': 'Cetak tampilan saat ini',

    // User Management Page
    'tenant_customer_management': 'Manajemen Penyewa & Pelanggan',
    'manage_tenant_data': 'Kelola data penyewa apartemen dan riwayat pelanggan',
    'currently_renting': 'Sedang Sewa',
    'no_booking_yet': 'Belum ada booking',
    'not_booked_yet': 'Belum Booking',
    'active_account': 'Akun Aktif',
    'blocked_account': 'Akun Diblokir',
    'property_info_not_available': 'Info Properti Tidak Tersedia',
    'tenant': 'Penyewa',
    'booking_status': 'Status Booking',

    // Activity Logs Page
    'loading_activity_logs': 'Memuat log aktivitas...',
    'monitor_system_activities': 'Pantau aktivitas sistem dan tindakan pengguna',
    'date_time': 'Tanggal/Waktu',
    'action': 'Aksi',
    'module': 'Modul',
    'type': 'Tipe',
    'system_activity_log': 'Log Aktivitas Sistem',
    'no_activity_logs_found': 'Tidak Ada Log Aktivitas',
    'no_system_activities_recorded': 'Belum ada aktivitas sistem yang tercatat',
    'try_adjusting_search_filter': 'Coba sesuaikan kriteria pencarian atau filter',

    // Settings Page - File Validation
    'file_size_max_2mb': 'Ukuran file harus kurang dari 2MB',
    'select_valid_image': 'Pilih file gambar yang valid (JPG, PNG, GIF, WebP)',
    'image_data_too_large': 'Data gambar terlalu besar untuk diproses',
    'invalid_image_format': 'Format data gambar tidak valid',
    'invalid_image_file': 'File gambar tidak valid',
    'failed_read_file': 'Gagal membaca file yang dipilih',
    'select_file_first': 'Pilih file terlebih dahulu',
    'profile_photo_updated': 'Foto profil berhasil diperbarui!',
    'failed_update_photo': 'Gagal memperbarui foto profil',
    'enter_valid_email': 'Masukkan alamat email yang valid',
    'name_min_2_chars': 'Nama harus minimal 2 karakter',
    'current_password_incorrect': 'Kata sandi saat ini salah',
    'account_updated_success': 'Informasi akun berhasil diperbarui! Anda sekarang dapat login dengan email baru.',
    'failed_update_account': 'Gagal memperbarui informasi akun',
    'passwords_not_match': 'Kata sandi baru tidak cocok',
    'password_min_6_chars': 'Kata sandi baru harus minimal 6 karakter',
    'password_changed_success': 'Kata sandi berhasil diubah! Gunakan kata sandi baru untuk login di masa mendatang.',
    'failed_change_password': 'Gagal mengubah kata sandi',

    // Backup & Restore
    'database_backup_restore': 'Backup & Restore Database',
    'export_database': 'Ekspor Database',
    'import_database': 'Impor Database',
    'download_sql_backup': 'Unduh backup SQL lengkap dari database Anda',
    'restore_from_sql': 'Pulihkan database dari file backup SQL',
    'export_sql_backup': 'Ekspor Backup SQL',
    'restore_from_sql_btn': 'Pulihkan dari SQL',
    'restoring': 'Memulihkan...',
    'select_sql_file': 'Pilih file SQL terlebih dahulu',
    'backup_warning': 'Memulihkan backup akan mengganti semua data saat ini. Pastikan untuk mengekspor backup sebelum memulihkan!',
    'warning': 'Peringatan',
    'backup_downloaded': 'Backup database berhasil diunduh!',
    'failed_export_backup': 'Gagal mengekspor backup database',
    'database_restored': 'Database berhasil dipulihkan!',
    'failed_restore_database': 'Gagal memulihkan database',
    'confirm_restore': '⚠️ PERINGATAN: Ini akan mengganti semua data saat ini! Apakah Anda yakin?',
    'select_valid_sql': 'Pilih file SQL yang valid'
  },
  en: {
    // Dashboard
    'good_morning': 'Good Morning',
    'good_afternoon': 'Good Afternoon',
    'good_evening': 'Good Evening',
    'good_night': 'Good Night',
    'real_time': 'Real Time',
    'admin_panel': 'Admin Panel',

    // Navigation
    'dashboard': 'Dashboard',
    'users': 'Users',
    'properties': 'Properties',
    'bookings': 'Bookings',
    'payments': 'Payments',
    'reports': 'Reports',
    'analytics': 'Analytics',
    'settings': 'Settings',
    'activity_logs': 'Activity Logs',
    'user_management': 'User Management',
    'management': 'Management',
    'system': 'System',
    'logout': 'Logout',

    // Stats
    'total_users': 'Total Users',
    'total_properties': 'Total Properties',
    'total_bookings': 'Total Bookings',
    'total_revenue': 'Total Revenue',
    'active_bookings': 'Active Bookings',
    'pending_bookings': 'Pending Bookings',
    'available_properties': 'Available Properties',
    'rented_properties': 'Rented Properties',
    'active': 'Active',
    'pending': 'Pending',
    'available': 'Available',
    'rented': 'Rented',

    // Dashboard specific
    'recent_bookings': 'Recent Bookings',
    'quick_actions': 'Quick Actions',
    'manage_users': 'Manage Users',
    'properties_management': 'Properties Management',
    'view_bookings': 'View Bookings',
    'view_payments': 'View Payments',
    'no_recent_bookings': 'No recent bookings',
    'view': 'View',
    'cards': 'Cards',
    'table': 'Table',
    'no_properties_found': 'No Properties Found',
    'try_adjusting_search': 'Try adjusting your search or filter criteria',
    'manage_all_property_listings': 'Manage all property listings and details',
    'last_30_days': 'Last 30 Days',
    'last_7_days': 'Last 7 Days',
    'last_90_days': 'Last 90 Days',
    'last_year': 'Last Year',
    'confirm_logout': 'Confirm Logout',
    'confirm_logout_message': 'Are you sure you want to logout from the system?',
    'yes_logout': 'Yes, Logout',
    'track_all_activities': 'Track all manager activities and system events',
    'all_activities': 'All Activities',
    'updates': 'Updates',
    'approvals': 'Approvals',
    'created': 'Created',
    'no_activities_found': 'No Activities Found',
    'no_activities_recorded': 'No system activities have been recorded yet',
    'no_activities_match': 'No activities match the selected filter',

    // Common
    'loading': 'Loading...',
    'no_data': 'No data available',
    'search': 'Search',
    'filter': 'Filter',
    'export': 'Export',
    'add_new': 'Add New',
    'edit': 'Edit',
    'delete': 'Delete',
    'save': 'Save',
    'cancel': 'Cancel',
    'confirm': 'Confirm',
    'status': 'Status',
    'actions': 'Actions',
    'all': 'All',
    'records': 'records',
    'name': 'Name',
    'email': 'Email',
    'phone': 'Phone',
    'address': 'Address',
    'date': 'Date',
    'amount': 'Amount',
    'description': 'Description',

    // All Pages
    'property_management': 'Property Management',
    'booking_management': 'Booking Management',
    'payment_management': 'Payment Management',
    'notification_center': 'Notification Center',
    'system_health': 'System Health',
    'backup_restore': 'Backup & Restore',
    'file_manager': 'File Manager',
    'messages': 'Messages',
    'notifications': 'Notifications',

    // User Management
    'manage_monitor_users': 'Manage and monitor user accounts with advanced controls',
    'add_user': 'Add User',
    'edit_user': 'Edit User',
    'suspend_user': 'Suspend User',
    'activate_user': 'Activate User',
    'user_details': 'User Details',
    'joined': 'Joined',
    'inactive': 'Inactive',

    // Properties
    'add_property': 'Add Property',
    'edit_property': 'Edit Property',
    'property_details': 'Property Details',
    'property_type': 'Property Type',
    'bedrooms': 'Bedrooms',
    'bathrooms': 'Bathrooms',
    'area': 'Area',
    'price': 'Price',
    'facilities': 'Facilities',
    'location': 'Location',
    'loading_properties': 'Loading properties...',
    'manage_all_properties': 'Manage all property listings and details',
    'mark_available': 'Mark Available',
    'size': 'Size',
    'image': 'Image',
    'property': 'Property',
    'maintenance': 'Maintenance',
    'manage_property_info': 'Manage property information and details',
    'property_name': 'Property Name',
    'property_city': 'City',
    'property_description': 'Description',
    'property_address': 'Address',
    'property_price': 'Price (Rp/month)',
    'property_bedrooms': 'Bedrooms',
    'property_bathrooms': 'Bathrooms',
    'property_size': 'Size (m²)',
    'property_image': 'Property Image',
    'property_status': 'Status',
    'enter_property_name': 'Enter property name',
    'enter_city': 'Enter city',
    'enter_description': 'Enter description',
    'enter_address': 'Enter full address',
    'optional': 'optional',
    'choose_image_file_property': 'Choose an image file (JPG, PNG, GIF, WebP, max 2MB, min 100x100px)',
    'new_image': 'New Image',
    'current_image': 'Current Image',
    'invalid_image': 'Invalid image',
    'selected': 'Selected',
    'create_property': 'Create Property',
    'update_property': 'Update Property',
    'saving': 'Saving...',
    'manage_user_info': 'Manage user account information',
    'profile_photo': 'Profile Photo',
    'new_photo': 'New Photo',
    'current_photo': 'Current Photo',
    'invalid_photo': 'Invalid image',
    'choose_image_file_user': 'Choose an image file (JPG, PNG, GIF, WebP, max 2MB, min 100x100px)',
    'selected_file': 'Selected',
    'full_name': 'Full Name',
    'enter_full_name': 'Enter full name',
    'email_address': 'Email Address',
    'enter_email': 'Enter email address',
    'password': 'Password',
    'enter_password': 'Enter password',
    'keep_current_password': 'Leave blank to keep current password',
    'phone_number': 'Phone Number',
    'account_status': 'Account Status',
    'create_user': 'Create User',
    'update_user': 'Update User',
    'manage_booking_info': 'Manage booking details',
    'edit_booking': 'Edit Booking',
    'add_booking': 'Add Booking',
    'select_user': 'Select User',
    'select_property': 'Select Property',
    'start_date': 'Start Date',
    'end_date': 'End Date',
    'end_date_after_start': 'End date must be after start date!',
    'total_cost': 'Total Cost',
    'nights': 'nights',
    'guests': 'Guests',
    'create_booking': 'Create Booking',
    'update_booking': 'Update Booking',
    'manage_payment_info': 'Manage payment information',
    'edit_payment': 'Edit Payment',
    'add_payment': 'Add Payment',
    'select_booking': 'Select Booking',
    'payment_amount': 'Amount',
    'payment_method_label': 'Payment Method',
    'select_payment_method': 'Select Payment Method',
    'transaction_id_label': 'Transaction ID',
    'optional_field': 'Optional',
    'user_label': 'User',
    'property_label': 'Property',
    'total_amount_label': 'Total Amount',
    'paid_amount': 'Paid Amount',
    'remaining_amount': 'Remaining',
    'max_amount': 'Max',
    'create_payment': 'Create Payment',
    'update_payment': 'Update Payment',
    'manage_all_bookings': 'Manage all booking requests and reservations',
    'booking': 'Booking',
    'user': 'User',
    'dates': 'Dates',
    'no_bookings_found': 'No Bookings Found',
    'approve': 'Approve',
    'rejected': 'Rejected',
    'completed': 'Completed',
    'approved': 'Approved',
    'cancelled': 'Cancelled',
    'manage_all_payments': 'Manage all payment transactions and receipts',
    'payment': 'Payment',
    'booking_id': 'Booking ID',
    'transaction_id': 'Transaction ID',
    'no_payments_found': 'No Payments Found',
    'mark_completed': 'Mark Completed',
    'failed': 'Failed',
    'loading_analytics': 'Loading analytics...',
    'revenue_per_property': 'Revenue per Property',
    'avg_per_property': 'Average per Property',
    'active_users': 'Active Users',
    'booking_statistics': 'Booking Statistics',
    'conversion_rate_label': 'Conversion Rate',
    'customer_retention': 'Customer Retention',
    'booking_revenue_trends': 'Booking & Revenue Trends (Last 6 Months)',
    'revenue_label': 'Revenue',
    'performance_summary': 'Performance Summary',
    'booking_growth': 'Booking Growth',
    'repeat_users': 'Repeat Users',
    'avg_occupancy': 'Average Occupancy',
    'top_properties_label': 'Top Properties',
    'occupancy': 'Occupancy',
    'average': 'Average',
    'total_properties_label': 'Total Properties',
    'units_available': 'Units available',
    'total_bookings_label': 'Total Bookings',
    'from_last_month': 'from last month',
    'occupancy_rate_label': 'Occupancy Rate',
    'avg_occupancy_label': 'Average occupancy',
    'total_revenue_label': 'Total Revenue',
    'growth': 'growth',
    'manage_account_settings': 'Manage your account settings and preferences',
    'profile_settings': 'Profile Settings',
    'current_profile_photo': 'Current Profile Photo',
    'photo_appears_header': 'This photo appears in the header',
    'recommended_square': 'Recommended: Square image, min 200x200px',
    'choose_new_photo': 'Choose New Profile Photo',
    'choose_image_file': 'Choose an image file (JPG, PNG, GIF, max 2MB)',
    'preview': 'Preview',
    'updating': 'Updating...',
    'update_photo': 'Update Photo',
    'account_information': 'Account Information',
    'role': 'Role',
    'enter_current_password': 'Enter your current password below to save changes',
    'enter_password_confirm': 'Enter current password to confirm changes',
    'enter_password_to_save': 'Enter your current password below to save changes',
    'confirm_new_password_placeholder': 'Confirm new password',
    'change_password': 'Change Password',
    'current_password': 'Current Password',
    'enter_current_pass': 'Enter current password',
    'new_password': 'New Password',
    'enter_new_password': 'Enter new password',
    'confirm_new_password': 'Confirm New Password',
    'confirm_new_pass': 'Confirm new password',
    'changing_password': 'Changing Password...',
    'confirm_profile_update': 'Confirm Profile Update',
    'confirm_profile_message': 'Are you sure you want to update your profile information? This will change your account details.',
    'update_profile': 'Update Profile',
    'confirm_password_change': 'Confirm Password Change',
    'confirm_password_message': 'Are you sure you want to change your password? You will need to use the new password for future logins.',
    'change_password_btn': 'Change Password',
    'no_data_export': 'No data to export',
    'export_failed': 'Export failed',
    'no_data_available': 'No data available',
    'pdf_export_failed': 'PDF export failed',
    'generating_pdf': 'Generating PDF...',
    'exporting': 'Exporting...',

    // Bookings
    'booking_details': 'Booking Details',
    'check_in': 'Check In',
    'check_out': 'Check Out',
    'guest_name': 'Guest Name',
    'total_amount': 'Total Amount',
    'booking_date': 'Booking Date',
    'confirmed': 'Confirmed',

    // Payments
    'payment_method': 'Payment Method',
    'payment_date': 'Payment Date',
    'paid': 'Paid',
    'unpaid': 'Unpaid',
    'refunded': 'Refunded',

    // Analytics
    'analytics_reports': 'Analytics & Reports',
    'arpu': 'ARPU',
    'customer_ltv': 'Customer LTV',
    'conversion_rate': 'Conversion Rate',
    'market_share': 'Market Share',
    'property_growth': 'Property Growth',
    'revenue_trend': 'Revenue Trend',
    'executive_summary': 'Executive Summary',
    'customer_segments': 'Customer Segments',
    'ai_insights': 'AI Insights',
    'top_properties': 'Top Properties',

    // Settings
    'general_settings': 'General Settings',
    'security_settings': 'Security Settings',
    'notification_settings': 'Notification Settings',
    'system_settings': 'System Settings',
    'language_settings': 'Language Settings',
    'theme_settings': 'Theme Settings',

    // Common Actions
    'reject': 'Reject',
    'suspend': 'Suspend',
    'restore': 'Restore',
    'download': 'Download',
    'upload': 'Upload',
    'refresh': 'Refresh',
    'close': 'Close',
    'open': 'Open',
    'submit': 'Submit',
    'reset': 'Reset',
    'clear': 'Clear',

    // Users Page
    'users_management': 'Users Management',
    'manage_user_accounts': 'Manage user accounts and permissions',
    'users_management_report': 'Users Management Report',
    'selected_users_export': 'Selected Users Export',
    'generated': 'Generated',
    'id': 'ID',
    'no_phone': 'No phone',
    'error_loading_users': 'Error Loading Users',
    'try_again': 'Try Again',
    'no_users_found': 'No Users Found',
    'adjust_search_filter': 'Try adjusting your search or filter criteria',
    'confirm_delete_user': 'Are you sure you want to delete this user?',
    'user_deleted_success': 'User deleted successfully!',
    'failed_delete_user': 'Failed to delete user',
    'deleted_successfully': 'deleted successfully',
    'failed_delete_users': 'Failed to delete users',
    'activated_successfully': 'activated successfully',
    'failed_activate_users': 'Failed to activate users',

    // Pagination
    'showing': 'Showing',
    'of': 'of',
    'back': 'Back',
    'next': 'Next',

    // Properties Page
    'properties_management_report': 'Properties Management Report',
    'confirm_delete_property': 'Are you sure you want to delete this property?',
    'property_deleted_success': 'Property deleted successfully!',
    'failed_delete_property': 'Failed to delete property',
    'failed_delete_properties': 'Failed to delete properties',
    'updated_successfully': 'updated successfully',
    'failed_update_properties': 'Failed to update properties',
    'br': 'BR',
    'ba': 'BA',

    // Bookings Page
    'confirm_delete_booking': 'Are you sure you want to delete this booking?',
    'booking_deleted_success': 'Booking deleted successfully',
    'failed_delete_booking': 'Failed to delete booking',
    'error_loading_data': 'Error Loading Data',
    'export_details': 'Export Details',
    'no_bookings_available': 'No booking data available yet',

    // Reports Page
    'sewaapartemen_analytics_report': 'SewaApartemen Analytics Report',
    'generated_on': 'Generated on',
    'comprehensive_business_reports': 'Comprehensive business reports and data insights',
    'monthly': 'Monthly',
    'quarterly': 'Quarterly',
    'yearly': 'Yearly',
    'revenue_overview': 'Revenue Overview',
    'transactions': 'transactions',
    'bookings_by_status': 'Bookings by Status',
    'properties_by_status': 'Properties by Status',
    'user_statistics': 'User Statistics',
    'new_this_month': 'New This Month',
    'export_reports': 'Export Reports',
    'export_to_pdf': 'Export to PDF',
    'download_comprehensive_report': 'Download comprehensive report',
    'export_data': 'Export Data',
    'download_raw_data_file': 'Download raw data file',
    'print_report': 'Print Report',
    'print_current_view': 'Print current view',

    // User Management Page
    'tenant_customer_management': 'Tenant & Customer Management',
    'manage_tenant_data': 'Manage apartment tenant data and customer history',
    'currently_renting': 'Currently Renting',
    'no_booking_yet': 'No booking yet',
    'not_booked_yet': 'Not Booked Yet',
    'active_account': 'Active Account',
    'blocked_account': 'Blocked Account',
    'property_info_not_available': 'Property Info Not Available',
    'tenant': 'Tenant',
    'booking_status': 'Booking Status',

    // Activity Logs Page
    'loading_activity_logs': 'Loading activity logs...',
    'monitor_system_activities': 'Monitor system activities and user actions',
    'date_time': 'Date/Time',
    'action': 'Action',
    'module': 'Module',
    'type': 'Type',
    'system_activity_log': 'System Activity Log',
    'no_activity_logs_found': 'No Activity Logs Found',
    'no_system_activities_recorded': 'No system activities have been recorded yet',
    'try_adjusting_search_filter': 'Try adjusting your search or filter criteria',

    // Settings Page - File Validation
    'file_size_max_2mb': 'File size must be less than 2MB',
    'select_valid_image': 'Please select a valid image file (JPG, PNG, GIF, WebP)',
    'image_data_too_large': 'Image data too large to process',
    'invalid_image_format': 'Invalid image data format',
    'invalid_image_file': 'Invalid image file',
    'failed_read_file': 'Failed to read the selected file',
    'select_file_first': 'Please select a file first',
    'profile_photo_updated': 'Profile photo updated successfully!',
    'failed_update_photo': 'Failed to update profile photo',
    'enter_valid_email': 'Please enter a valid email address',
    'name_min_2_chars': 'Name must be at least 2 characters long',
    'current_password_incorrect': 'Current password is incorrect',
    'account_updated_success': 'Account information updated successfully! You can now login with your new email.',
    'failed_update_account': 'Failed to update account information',
    'passwords_not_match': 'New passwords do not match',
    'password_min_6_chars': 'New password must be at least 6 characters',
    'password_changed_success': 'Password changed successfully! Please use the new password for future logins.',
    'failed_change_password': 'Failed to change password',

    // Backup & Restore
    'database_backup_restore': 'Database Backup & Restore',
    'export_database': 'Export Database',
    'import_database': 'Import Database',
    'download_sql_backup': 'Download a complete SQL backup of your database',
    'restore_from_sql': 'Restore database from SQL backup file',
    'export_sql_backup': 'Export SQL Backup',
    'restore_from_sql_btn': 'Restore from SQL',
    'restoring': 'Restoring...',
    'select_sql_file': 'Please select a SQL file first',
    'backup_warning': 'Restoring a backup will replace all current data. Make sure to export a backup before restoring!',
    'warning': 'Warning',
    'backup_downloaded': 'Database backup downloaded successfully!',
    'failed_export_backup': 'Failed to export database backup',
    'database_restored': 'Database restored successfully!',
    'failed_restore_database': 'Failed to restore database',
    'confirm_restore': '⚠️ WARNING: This will replace all current data! Are you sure?',
    'select_valid_sql': 'Please select a valid SQL file'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'id';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'id' ? 'en' : 'id');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};